import * as React from "react";
import { X, Trash2, Image as ImageIcon, Save, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/app/components/button";
import { Input } from "@/app/components/input";
import { Textarea } from "@/app/components/textarea";
import { Select } from "@/app/components/select";
import type { MyAd } from "@/app/components/my-ads-page";
import {
  getAdUploadUrl,
  uploadFileToSignedUrl,
  isAllowedAdImageType,
  getCategories,
  patchAd,
  apiAdToAd,
} from "@/app/api/auth-client";
import { compressAdImageForUpload } from "@/app/utils/compress-image-to-max-bytes";
import { parsePositiveShippingNumber } from "@/app/utils/shipping-form";
import type { Category } from "@/app/api/auth-client";
import {
  CategoryListingAttributeFields,
  attributeDefsForCategory,
  buildListingAttributesPayload,
  validateListingAttributeForm,
  listingAttrValueToString,
} from "@/app/components/category-listing-attributes";
import { buildCategoryTree } from "@/app/utils/category-tree";
import { formatBrlPriceInputFromReais, maskBrlPriceInputChange, parseBrlPriceInput } from "@/config/locale";
import { AdImage } from "@/app/components/common/ad-image";
import { isStoragePath } from "@/app/utils/ad-utils";
import { ListingQualityPanel } from "@/app/components/listing-quality-panel";
import { useListingQualityPreview } from "@/app/hooks/use-listing-quality-preview";
import { previewUserImagesFromCount } from "@/app/utils/listing-quality-utils";
import type { ListingQualityPreviewPayload } from "@/app/api/auth-client";

interface EditAdModalProps {
  ad: MyAd;
  onClose: () => void;
  onSave: (ad: MyAd) => void;
}

/** Fallback when GET /catalog/categories fails. */
const FALLBACK_CATEGORIES = [
  "Eletrônicos",
  "Móveis",
  "Roupas",
  "Esportes",
  "Livros",
  "Brinquedos",
  "Música",
  "Outros",
];

const MAX_IMAGES = 5;

/** Map ad.condition display value to form value. */
function conditionToForm(condition?: string): "used" | "new" {
  const c = (condition ?? "").toLowerCase();
  if (c === "new" || c === "novo") return "new";
  return "used";
}

export function EditAdModal({ ad, onClose, onSave }: EditAdModalProps) {
  const toStr = (n: number | undefined): string =>
    n != null && n > 0 ? String(n) : "";

  const [formData, setFormData] = React.useState({
    title: ad.title,
    description: ad.description,
    price: formatBrlPriceInputFromReais(Number(ad.price)) || "",
    category: ad.category_id ?? "",
    condition: conditionToForm(ad.condition),
    acceptsOffers: ad.acceptsOffers ?? true,
    shippingWeightKg: toStr(ad.shipping_weight_kg),
    shippingWidthCm: toStr(ad.shipping_width_cm),
    shippingHeightCm: toStr(ad.shipping_height_cm),
    shippingLengthCm: toStr(ad.shipping_length_cm),
  });

  const [listingAttrForm, setListingAttrForm] = React.useState<Record<string, string>>({});

  const [categories, setCategories] = React.useState<Category[] | null>(null);
  const [categoriesError, setCategoriesError] = React.useState(false);
  const categoryTree = React.useMemo(
    () => (categories && categories.length > 0 ? buildCategoryTree(categories) : []),
    [categories]
  );

  const listingAttrDefs = React.useMemo(
    () => attributeDefsForCategory(categories, formData.category),
    [categories, formData.category]
  );

  React.useEffect(() => {
    getCategories()
      .then((list) => setCategories(list))
      .catch(() => setCategoriesError(true));
  }, []);

  const [images, setImages] = React.useState<string[]>(() => {
    if (ad.user_images && ad.user_images.length > 0) return [...ad.user_images];
    if (ad.images && ad.images.length > 0) return [...ad.images];
    return ad.image ? [ad.image] : [];
  });
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const qualityPreviewPayload = React.useMemo((): ListingQualityPreviewPayload | null => {
    if (!formData.title.trim()) return null;
    const price = parseBrlPriceInput(formData.price);
    const sw = parsePositiveShippingNumber(formData.shippingWeightKg);
    const swi = parsePositiveShippingNumber(formData.shippingWidthCm);
    const sh = parsePositiveShippingNumber(formData.shippingHeightCm);
    const sl = parsePositiveShippingNumber(formData.shippingLengthCm);
    const storageImages = images.filter((img) => isStoragePath(img));
    const photoCount = storageImages.length > 0 ? storageImages.length : images.length;
    return {
      title: formData.title,
      description: formData.description,
      price: Number.isNaN(price) || price <= 0 ? 0 : price,
      category: formData.category,
      condition: formData.condition,
      user_images:
        storageImages.length > 0
          ? storageImages
          : previewUserImagesFromCount(photoCount),
      attributes: buildListingAttributesPayload(listingAttrDefs, listingAttrForm),
      ...(sw != null ? { shipping_weight_kg: sw } : {}),
      ...(swi != null ? { shipping_width_cm: swi } : {}),
      ...(sh != null ? { shipping_height_cm: sh } : {}),
      ...(sl != null ? { shipping_length_cm: sl } : {}),
    };
  }, [formData, images, listingAttrForm, listingAttrDefs]);

  const {
    result: qualityPreview,
    loading: qualityLoading,
    error: qualityError,
  } = useListingQualityPreview(qualityPreviewPayload);

  // Sync form when ad changes
  React.useEffect(() => {
    setFormData({
      title: ad.title,
      description: ad.description,
      price: formatBrlPriceInputFromReais(Number(ad.price)) || "",
      category: ad.category_id ?? "",
      condition: conditionToForm(ad.condition),
      acceptsOffers: ad.acceptsOffers ?? true,
      shippingWeightKg: toStr(ad.shipping_weight_kg),
      shippingWidthCm: toStr(ad.shipping_width_cm),
      shippingHeightCm: toStr(ad.shipping_height_cm),
      shippingLengthCm: toStr(ad.shipping_length_cm),
    });
    const nextAttrs: Record<string, string> = {};
    if (ad.listing_attributes && typeof ad.listing_attributes === "object") {
      for (const [k, v] of Object.entries(ad.listing_attributes)) {
        nextAttrs[k] = listingAttrValueToString(v);
      }
    }
    setListingAttrForm(nextAttrs);
    if (ad.user_images?.length) {
      setImages([...ad.user_images]);
    } else if (ad.images?.length) {
      setImages([...ad.images]);
    } else if (ad.image) {
      setImages([ad.image]);
    }
  }, [ad]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remainingSlots = MAX_IMAGES - images.length;
    const filesToProcess = Array.from(files).slice(0, remainingSlots);

    for (const file of filesToProcess) {
      const type = (file.type || "").toLowerCase();
      if (!isAllowedAdImageType(type)) {
        alert(`${file.name}: use apenas JPG ou PNG.`);
        continue;
      }

      const compressed = await compressAdImageForUpload(file);
      if (!compressed.ok) {
        alert(`${file.name}: ${compressed.error}`);
        continue;
      }
      const resized = compressed.file;

      if (ad.id) {
        setUploadError(null);
        setUploading(true);
        try {
          const contentType = (resized.type || "").toLowerCase() === "image/png" ? "image/png" : "image/jpeg";
          const { upload_url, storage_path } = await getAdUploadUrl(ad.id, contentType);
          await uploadFileToSignedUrl(upload_url, resized, contentType);
          setImages((prev) => (prev.length < MAX_IMAGES ? [...prev, storage_path] : prev));
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Falha ao enviar imagem.";
          setUploadError(msg);
        } finally {
          setUploading(false);
        }
      } else {
        const reader = new FileReader();
        reader.onload = (event) => {
          const result = event.target?.result as string;
          if (result) {
            setImages((prev) => (prev.length < MAX_IMAGES ? [...prev, result] : prev));
          }
        };
        reader.readAsDataURL(resized);
      }
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleMoveImage = (index: number, direction: "left" | "right") => {
    const newImages = [...images];
    const targetIndex = direction === "left" ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= images.length) return;
    
    [newImages[index], newImages[targetIndex]] = [newImages[targetIndex], newImages[index]];
    setImages(newImages);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    const price = parseBrlPriceInput(formData.price);
    if (Number.isNaN(price) || price <= 0) {
      setSaveError("Preço inválido.");
      return;
    }
    const shippingWeightKg = parsePositiveShippingNumber(formData.shippingWeightKg);
    const shippingWidthCm = parsePositiveShippingNumber(formData.shippingWidthCm);
    const shippingHeightCm = parsePositiveShippingNumber(formData.shippingHeightCm);
    const shippingLengthCm = parsePositiveShippingNumber(formData.shippingLengthCm);
    if (
      shippingWeightKg == null ||
      shippingWidthCm == null ||
      shippingHeightCm == null ||
      shippingLengthCm == null
    ) {
      setSaveError(
        "Peso (kg) e dimensões (largura, altura e comprimento em cm) são obrigatórios para cotação de frete. Use valores maiores que zero."
      );
      return;
    }
    const attrErr = validateListingAttributeForm(listingAttrDefs, listingAttrForm);
    if (attrErr) {
      setSaveError(attrErr);
      return;
    }
    const storagePaths = images.filter((s): s is string => isStoragePath(s));

    setSaving(true);
    try {
      const attributesPayload = buildListingAttributesPayload(listingAttrDefs, listingAttrForm);
      const patchPayload: Parameters<typeof patchAd>[1] = {
        title: formData.title,
        description: formData.description,
        price,
        category_id: formData.category || undefined,
        condition: formData.condition,
        accepts_offers: formData.acceptsOffers,
        user_images: storagePaths.length > 0 ? storagePaths : undefined,
        shipping_weight_kg: shippingWeightKg,
        shipping_width_cm: shippingWidthCm,
        shipping_height_cm: shippingHeightCm,
        shipping_length_cm: shippingLengthCm,
        attributes: attributesPayload,
      };

      const updated = await patchAd(ad.id, patchPayload);
      const base = apiAdToAd(updated);
      const merged: MyAd = {
        ...ad,
        ...base,
        user_images: updated.user_images ?? storagePaths,
        status: ad.status,
        apiListingStatus: (updated.status ?? ad.apiListingStatus ?? "active").toLowerCase().trim(),
        discoveryEligible: updated.discovery_eligible !== false,
        listingQualityScore: updated.listing_quality_score,
        listingQualityTier: updated.listing_quality_tier,
        listingQualityVersion: updated.listing_quality_version,
        views: ad.views,
        favorites: ad.favorites,
        shipping_weight_kg: updated.shipping_weight_kg,
        shipping_width_cm: updated.shipping_width_cm,
        shipping_height_cm: updated.shipping_height_cm,
        shipping_length_cm: updated.shipping_length_cm,
      };
      onSave(merged);
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="flex items-center gap-2">
              <Save className="w-5 h-5" />
              Editar Anúncio
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Atualize as informações do seu anúncio
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <ListingQualityPanel
            result={qualityPreview}
            loading={qualityLoading}
            error={qualityError}
            persistedScore={ad.listingQualityScore}
            persistedTier={ad.listingQualityTier}
          />
          <div>
            <label className="block mb-2">Título *</label>
            <Input
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="ex: Câmera Vintage em Ótimo Estado"
            />
          </div>

          <div>
            <label className="block mb-2">Descrição *</label>
            <Textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descreva seu item em detalhes..."
              rows={6}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-2">Preço (R$) *</label>
              <Input
                type="text"
                inputMode="numeric"
                required
                autoComplete="off"
                value={formData.price}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    price: maskBrlPriceInputChange(e.target.value),
                  })
                }
                placeholder="0,00"
                className="tabular-nums"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Digite apenas números; o valor é formatado em reais (ex.: 9.000,00).
              </p>
            </div>

            <div>
              <label className="block mb-2">Categoria *</label>
              <Select
                value={formData.category}
                onChange={(e) => {
                  setFormData({ ...formData, category: e.target.value });
                  setListingAttrForm({});
                }}
              >
                {categoryTree.length > 0 ? (
                  <>
                    <option value="">Selecione uma categoria</option>
                    {categoryTree.map(({ root, children }) => (
                      <optgroup key={root.category_id} label={root.name}>
                        {children.length > 0 ? (
                          children.map((c) => (
                            <option key={c.category_id} value={c.category_id}>
                              {c.name}
                            </option>
                          ))
                        ) : (
                          <option value={root.category_id}>{root.name}</option>
                        )}
                      </optgroup>
                    ))}
                  </>
                ) : (
                  FALLBACK_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))
                )}
              </Select>
              {categoriesError && (
                <p className="text-xs text-muted-foreground mt-1">
                  Lista padrão de categorias (serviço indisponível).
                </p>
              )}
            </div>
          </div>

          <CategoryListingAttributeFields
            defs={listingAttrDefs}
            values={listingAttrForm}
            onChange={(key, value) =>
              setListingAttrForm((prev) => ({ ...prev, [key]: value }))
            }
          />

          {/* Condition: used or new */}
          <div>
            <label className="block mb-2">Condição do produto *</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="condition-edit"
                  value="used"
                  checked={formData.condition === "used"}
                  onChange={() => setFormData({ ...formData, condition: "used" })}
                  className="w-4 h-4 text-primary border-2 border-border focus:ring-2 focus:ring-primary focus:ring-offset-2"
                />
                <span>Usado</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="condition-edit"
                  value="new"
                  checked={formData.condition === "new"}
                  onChange={() => setFormData({ ...formData, condition: "new" })}
                  className="w-4 h-4 text-primary border-2 border-border focus:ring-2 focus:ring-primary focus:ring-offset-2"
                />
                <span>Novo</span>
              </label>
            </div>
          </div>

          {/* Shipping dimensions and weight (required for freight quote) */}
          <div className="space-y-3">
            <p className="text-sm font-medium">Dimensões e peso (frete) *</p>
            <p className="text-xs text-muted-foreground">
              Obrigatório para cotação e envio. Informe peso em kg e medidas da embalagem em cm (valores &gt; 0).
            </p>
            <div
              className="flex gap-3 rounded-lg border border-amber-300/90 bg-amber-50 px-3 py-2.5 text-xs text-amber-950 dark:border-amber-600/60 dark:bg-amber-950/35 dark:text-amber-50"
              role="note"
            >
              <AlertTriangle
                className="w-4 h-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5"
                aria-hidden
              />
              <p>
                <span className="font-semibold text-amber-900 dark:text-amber-100">Importante: </span>
                para maior precisão no cálculo do frete, insira as medidas que possam incluir a embalagem do produto.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-xs text-muted-foreground">Peso (kg) *</label>
                <Input
                  type="text"
                  inputMode="decimal"
                  required
                  autoComplete="off"
                  placeholder="ex: 1,5 ou 1.5"
                  value={formData.shippingWeightKg}
                  onChange={(e) => setFormData({ ...formData, shippingWeightKg: e.target.value })}
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-muted-foreground">Largura (cm) *</label>
                <Input
                  type="text"
                  inputMode="decimal"
                  required
                  autoComplete="off"
                  placeholder="ex: 30"
                  value={formData.shippingWidthCm}
                  onChange={(e) => setFormData({ ...formData, shippingWidthCm: e.target.value })}
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-muted-foreground">Altura (cm) *</label>
                <Input
                  type="text"
                  inputMode="decimal"
                  required
                  autoComplete="off"
                  placeholder="ex: 20"
                  value={formData.shippingHeightCm}
                  onChange={(e) => setFormData({ ...formData, shippingHeightCm: e.target.value })}
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-muted-foreground">Comprimento (cm) *</label>
                <Input
                  type="text"
                  inputMode="decimal"
                  required
                  autoComplete="off"
                  placeholder="ex: 40"
                  value={formData.shippingLengthCm}
                  onChange={(e) => setFormData({ ...formData, shippingLengthCm: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Accepts Offers Checkbox */}
          <div className="bg-accent/30 rounded-lg p-4 border border-border">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.acceptsOffers}
                onChange={(e) => setFormData({ ...formData, acceptsOffers: e.target.checked })}
                className="mt-1 w-5 h-5 text-primary border-2 border-border rounded focus:ring-2 focus:ring-primary focus:ring-offset-2 cursor-pointer"
              />
              <div className="flex-1">
                <p className="font-medium text-sm">Aceitar Ofertas</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Permitir que outros usuários façam ofertas em dinheiro ou trocas por produtos no seu anúncio.
                </p>
              </div>
            </label>
          </div>

          {/* Images Section */}
          <div>
            <label className="block mb-2">
              Fotos do Produto ({images.length}/{MAX_IMAGES})
            </label>
            
            {/* Upload Button */}
            {images.length < MAX_IMAGES && (
              <div className="mb-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="image-upload-edit"
                  disabled={uploading}
                />
                <label
                  htmlFor="image-upload-edit"
                  className={`flex items-center justify-center gap-3 w-full p-6 border-2 border-dashed border-border rounded-xl transition-all group ${
                    uploading ? "opacity-60 cursor-not-allowed" : "hover:border-primary hover:bg-accent/50 cursor-pointer"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    {uploading ? (
                      <Loader2 className="w-6 h-6 text-primary animate-spin" />
                    ) : (
                      <div className="p-3 bg-primary/10 rounded-full group-hover:bg-primary/20 transition-colors">
                        <ImageIcon className="w-6 h-6 text-primary" />
                      </div>
                    )}
                    <div className="text-center">
                      <p className="font-medium text-sm">
                        {uploading ? "Enviando..." : "Clique para adicionar mais fotos"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        JPG ou PNG, até 5 MB • máx. {MAX_IMAGES} fotos • {MAX_IMAGES - images.length} restantes
                      </p>
                    </div>
                  </div>
                </label>
                {uploadError && (
                  <p className="text-sm text-destructive mt-2">{uploadError}</p>
                )}
              </div>
            )}

            {/* Image Grid */}
            {images.length > 0 && (
              <div className="grid grid-cols-4 gap-3">
                {images.map((img, index) => (
                  <div key={index} className="relative group aspect-square">
                    <AdImage
                      src={img}
                      alt={`Imagem ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg border-2 border-border"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                      {/* Move left button */}
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => handleMoveImage(index, "left")}
                          className="p-1.5 bg-white text-gray-900 rounded-md hover:bg-gray-200 transition-colors text-xs"
                          title="Mover para esquerda"
                        >
                          ←
                        </button>
                      )}
                      
                      {/* Delete button */}
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="p-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                        title="Remover foto"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>

                      {/* Move right button */}
                      {index < images.length - 1 && (
                        <button
                          type="button"
                          onClick={() => handleMoveImage(index, "right")}
                          className="p-1.5 bg-white text-gray-900 rounded-md hover:bg-gray-200 transition-colors text-xs"
                          title="Mover para direita"
                        >
                          →
                        </button>
                      )}
                    </div>
                    {index === 0 && (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-primary text-primary-foreground text-xs rounded-md font-medium shadow-md">
                        Principal
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-muted-foreground mt-2">
              {images.length === 0 
                ? `Adicione até ${MAX_IMAGES} fotos do seu produto. A primeira será a imagem principal.`
                : `A primeira foto será a imagem principal do anúncio. Use as setas para reordenar.`
              }
            </p>
          </div>

          {saveError && (
            <p className="text-sm text-destructive" role="alert">
              {saveError}
            </p>
          )}

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button type="submit" className="flex-1" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Alterações
                </>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}